import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Grid,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Collapse,
  IconButton as MuiIconButton,
  TextField,
} from "@mui/material";
import MoneyTree from "../../images/png/Money-removebg-preview.png";
import Chart from "chart.js/auto";
import { ReportData, FormData, calculateReport } from "./calculateReport";
import { ArrowBack, GetApp, ExpandMore } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

import { AppDispatch, RootState } from "../../stores";
import { getMyAddWhiteLabel } from "../../features/add-white-label-module/addWhiteLabelAction";
import {
  downloadReport,
  ReportDetails,
  WhiteLabelConfig,
} from "../../utils/downloadReport";
import {
  GrantCostDetail,
  updateSiteReportData,
} from "../../features/generate-report-module/reportAction";

// Styled components (unchanged)
const Container = styled(Box)(({ theme }) => ({
  minHeight: "95vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const Content = styled(Box)<{ component?: React.ElementType }>(({ theme }) => ({
  width: "100%",
  maxWidth: "auto",
  padding: theme.spacing(2),
  marginTop: theme.spacing(0),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0),
    maxWidth: "100%",
    marginTop: theme.spacing(0),
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: "35px",
  fontWeight: 700,
  color: "#4b5b5d",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
  },
}));

const StyledBackButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "#f2efe9",
  color: "#455354",
  transition: "background 0.24s ease-in-out, color 0.24s ease-in-out",
  "&:hover": {
    background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
    color: "#f2efe9",
  },
}));

const ActionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  [theme.breakpoints.down("sm")]: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
}));

const ProfitContent = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(3),
  background: "linear-gradient(to left, #f4efe6, #4b5b5d)",
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
    flexDirection: "column",
    gap: theme.spacing(2),
    alignItems: "flex-start",
  },
}));

const TitleText = styled(Typography)(({ theme }) => ({
  fontSize: "2.8rem",
  fontWeight: 700,
  fontFamily: '"Libre Baskerville", serif',
  color: "#e8e5dc",
  [theme.breakpoints.down("md")]: {
    fontSize: "2.2rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.6rem",
  },
}));

const SubTitleText = styled(Typography)(({ theme }) => ({
  fontSize: "1.2rem",
  fontWeight: 500,
  fontFamily: '"Libre Baskerville", serif',
  color: "#e8e5dc",
  [theme.breakpoints.down("md")]: {
    fontSize: "1rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.9rem",
  },
}));

const BulletList = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  "& li": {
    color: "#e8e5dc",
    fontSize: "1rem",
    fontFamily: '"Libre Baskerville", serif',
    fontWeight: 400,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    "&::before": {
      content: '"•"',
      color: "#e8e5dc",
      fontSize: "1.5rem",
      lineHeight: "1rem",
    },
    [theme.breakpoints.down("md")]: {
      fontSize: "0.95rem",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.85rem",
    },
  },
}));

const MoneyTreeImage = styled("img")(({ theme }) => ({
  maxWidth: "270px",
  height: "auto",
  [theme.breakpoints.down("md")]: {
    maxWidth: "180px",
  },
  [theme.breakpoints.down("sm")]: {
    maxWidth: "120px",
  },
}));

const MapContainer = styled(Box)(({ theme }) => ({
  height: "300px",
  width: "100%",
  border: "1px solid #ccc",
  borderRadius: "5px",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    height: "250px",
  },
  [theme.breakpoints.down("sm")]: {
    height: "200px",
  },
}));

const Card = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8f8f8",
  borderRadius: "8px",
  padding: theme.spacing(2),
  width: "100%",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: "1.8rem",
  fontWeight: 600,
  color: "#4b5b5d",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("md")]: {
    fontSize: "1.2rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: "#757575",
  fontSize: "1rem",
  fontWeight: 500,
  padding: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.8rem",
    padding: theme.spacing(0.5),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const StyledTableDataCell = styled(TableCell)(({ theme }) => ({
  color: "#757575",
  fontSize: "0.9rem",
  fontWeight: 500,
  padding: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.7rem",
    padding: theme.spacing(0.5),
  },
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  height: "400px",
  width: "100%",
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: "#ffffff",
  border: "1px solid #eee",
  borderRadius: "8px",
  padding: theme.spacing(2),
  boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
  [theme.breakpoints.down("md")]: {
    height: "350px",
  },
  [theme.breakpoints.down("sm")]: {
    height: "280px",
    padding: theme.spacing(1),
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(to right, #4b5b5d, #758d8e)",
  color: "#f2efe9",
  fontWeight: 500,
  padding: theme.spacing(1, 3),
  borderRadius: "3px",
  textTransform: "none",
  fontSize: "0.9rem",
  "&:hover": {
    background: "linear-gradient(to right, #758d8e, #4b5b5d)",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.9rem",
    padding: theme.spacing(0.8, 2),
  },
}));

const ExpandMoreIcon = styled(ExpandMore)(({ theme }) => ({
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.short,
  }),
}));

const NEGATIVE_VALUE_CATEGORIES = new Set<string>([
  "Install Cost",
  "Hardware Cost",
  "Network Cost",
  "Maintenance",
  "Utility Bill (Energy + Demand + Fixed)",
]);

const addNegativePrefix = (
  value: string | number | undefined | null
): string => {
  if (value === null || value === undefined) return "-0";
  const sourceString =
    typeof value === "number" || typeof value === "bigint"
      ? value.toString()
      : `${value}`;
  const trimmed = sourceString.trim();
  if (!trimmed) return "-0";
  if (trimmed.startsWith("-")) return trimmed;
  if (trimmed.startsWith("$")) {
    return `-$${trimmed.slice(1)}`;
  }
  return `-${trimmed}`;
};

const formatNegativeCategoryValue = (
  category: string,
  value: string | number | undefined | null
): string | number => {
  const isNegativeCategory = NEGATIVE_VALUE_CATEGORIES.has(category);

  if (value === null || value === undefined) {
    return isNegativeCategory ? addNegativePrefix(value) : "";
  }

  if (!isNegativeCategory) {
    return value;
  }

  return addNegativePrefix(value);
};

const stripNegativePrefix = (
  category: string,
  value: string | number
): string | number => {
  if (!NEGATIVE_VALUE_CATEGORIES.has(category)) {
    return value;
  }

  if (typeof value === "number") {
    return Math.abs(value);
  }

  const trimmed = value.trim();
  if (!trimmed.startsWith("-")) {
    return value;
  }

  if (trimmed.startsWith("-$")) {
    return `$${trimmed.slice(2)}`;
  }

  return trimmed.slice(1);
};

export interface ViewGeneratedReportState {
  formData: FormData;
  reportData: ReportData;
  utilization: number;
  utilityRate: number;
  utilityDetails?: {
    state: string;
    zipCode: string;
    utilityName: string;
    utilityId: string;
    scheduleId: string;
    totalCost: number;
  };
  energyCosts?: {
    energy: number[];
    energyTime: number[];
    incrementalEnergy: number[];
    demand: number[];
    demandTime: number[];
    incrementalDemand: number[];
    serviceCharge: number[];
    otherCharges: number[];
    total: number[];
  };
  grantCostDetails?: GrantCostDetail[];
  excludeIncentives: boolean;
  disableUtilityEnergyInfo?: boolean;

  // Add tax benefits properties
  applyTaxBenefits?: boolean;
  businessTaxRate?: number;
  section179Limit?: number;
  bonusDepreciationPct?: number;
  qualifyingSpend?: number;
  taxSavings?: number;
  upfrontAfterTax?: number;

  // Add profit sharing properties
  customerSplit?: string;
  softwareProviderSplit?: string;
  operatorSplit?: string;
  customerProfit?: number;
  softwareProviderProfit?: number;
  operatorProfit?: number;
}

interface EditableCellProps {
  value: string | number;
  onChange: (newVal: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

const EditableCell: React.FC<EditableCellProps> = ({
  value,
  onChange,
  disabled,
  readOnly = false,
}) => {
  const [local, setLocal] = useState(String(value));
  const [error, setError] = useState("");

  const validateInput = (input: string): boolean => {
    if (readOnly) return true;

    // Check if it's empty
    if (!input.trim()) {
      setError("Value cannot be empty");
      return false;
    }

    // For numeric fields, validate the number
    if (typeof value === "string" && value.startsWith("$")) {
      const cleaned = input.replace(/[$,]/g, "");
      const numValue = parseFloat(cleaned);

      if (isNaN(numValue)) {
        setError("Please enter a valid number");
        return false;
      }

      if (numValue < 0) {
        setError("Value cannot be negative");
        return false;
      }
    }

    setError("");
    return true;
  };

  const handleBlur = () => {
    if (readOnly || disabled) return;

    if (validateInput(local)) {
      // Check if it's a currency value (starts with $)
      if (typeof value === "string" && value.startsWith("$")) {
        const cleaned = local.replace(/[$,]/g, "");
        const numValue = parseFloat(cleaned);

        if (!isNaN(numValue)) {
          onChange(`$${Math.round(numValue).toLocaleString()}`);
        } else {
          onChange(local);
        }
      } else {
        // For non-currency values
        if (!isNaN(Number(local))) {
          onChange(String(Number(local)));
        } else {
          onChange(local);
        }
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocal(newValue);
    validateInput(newValue);
  };

  // If disabled OR readOnly, show as non-editable
  if (disabled || readOnly) {
    return <StyledTableDataCell>{value}</StyledTableDataCell>;
  }

  return (
    <TableCell sx={{ p: 0 }}>
      <TextField
        size="small"
        value={local}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            handleBlur();
          }
        }}
        fullWidth
        variant="outlined"
        error={!!error}
        helperText={error}
        sx={{
          "& .MuiInputBase-input": {
            fontSize: "0.85rem",
            py: 0.5,
            color:
              typeof value === "string" && value.startsWith("$")
                ? "#2e7d32"
                : "inherit",
          },
        }}
      />
    </TableCell>
  );
};
export interface ViewGeneratedReportProps {
  state?: ViewGeneratedReportState;
  onBack?: () => void;
}

const ViewGeneratedReport: React.FC<ViewGeneratedReportProps> = ({
  state: propsState,
  onBack,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const incomingState =
    propsState || (location.state as ViewGeneratedReportState) || {};
  const savedReport = (incomingState as any)?.savedReport as
    | {
        _id?: string;
        reportName: string;
        location?: string;
        zipCode?: string;
        netProfit: number;
        utilization: number;
        utilityRate: number;
        createdAt?: string;
        financialTable?: Array<{ category: string; value: string | number }>;
        breakEvenCashFlow?: { labels: string[]; data: number[] };
        revenueCost?: {
          labels: string[];
          datasets: Array<{ label: string; data: number[] }>;
        };
        co2Savings?: {
          labels: string[];
          datasets: Array<{ label: string; data: number[] }>;
        };
        utilityDetails?: {
          state: string;
          zipCode: string;
          utilityName: string;
          utilityId: string;
          scheduleId: string;
          totalCost: number;
        };
        energyCosts?: {
          energy: number[];
          energyTime: number[];
          incrementalEnergy: number[];
          demand: number[];
          demandTime: number[];
          incrementalDemand: number[];
          serviceCharge: number[];
          otherCharges: number[];
          total: number[];
        };
        grantCostDetails?: GrantCostDetail[];
        excludeIncentives?: boolean;
        disableUtilityEnergyInfo?: boolean;

        // Tax benefits fields
        applyTaxBenefits?: boolean;
        businessTaxRate?: number;
        section179Limit?: number;
        bonusDepreciationPct?: number;
        qualifyingSpend?: number;
        taxSavings?: number;
        upfrontAfterTax?: number;
      }
    | undefined;
  const viewingSavedReportOnly = !!savedReport && !incomingState.formData;
  const [utilization] = useState<number>(incomingState.utilization ?? 0.15);
  const [utilityRate] = useState<number>(incomingState.utilityRate ?? 0.18);
  const { formData } = incomingState;
  const reportData = useMemo<ReportData | undefined>(() => {
    if (savedReport) return undefined;
    if (!formData) return undefined;
    return calculateReport(formData, utilization, utilityRate || 0);
  }, [formData, utilization, utilityRate, savedReport]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mapRef = useRef<HTMLDivElement>(null);
  const breakEvenChartRef = useRef<HTMLCanvasElement>(null);
  const revenueCostChartRef = useRef<HTMLCanvasElement>(null);
  const co2SavingsChartRef = useRef<HTMLCanvasElement>(null);
  const breakEvenChartInstance = useRef<Chart | null>(null);
  const revenueCostChartInstance = useRef<Chart | null>(null);
  const co2SavingsChartInstance = useRef<Chart | null>(null);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [mapLoaded, setMapLoaded] = useState<boolean>(false);
  const [downloadFormat, setDownloadFormat] = useState<"PDF" | "WL">("PDF");
  const [mapView, setMapView] = useState<
    "streetview" | "roadmap" | "satellite"
  >("streetview");
  const { whiteLabel, loading: whiteLabelLoading } = useSelector(
    (state: RootState) => state.addWhiteLabel
  );

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const currentPlanName = (
    userData?.subscription?.planName || ""
  ).toLowerCase();
  const isStarterPlan = currentPlanName === "core";
  const [, setPosition] = useState<google.maps.LatLng | null>(null);
  const [streetViewAvailable, setStreetViewAvailable] =
    useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "info" | "warning" | "error" | "success"
  >("info");
  const [expandedRows, setExpandedRows] = useState<{ [key: number]: boolean }>(
    {}
  );

  const [isEditing, setIsEditing] = useState(false);
  const [editedTable, setEditedTable] = useState<
    Array<{ category: string; value: string | number }>
  >([]);
  // Add these new states for recalculated values
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalculatedNetProfit, setRecalculatedNetProfit] = useState<
    number | null
  >(null);
  const [, setRecalculatedValues] = useState<{
    install?: number;
    hardware?: number;
    network?: number;
    maintenance?: number;
    utilityCost5?: number;
    grant?: number;
    netProfit5?: number;
  }>({});
  const [recalculationTimeout, setRecalculationTimeout] =
    useState<NodeJS.Timeout | null>(null);

  const defaultUtilityDetails = {
    state: "--",
    zipCode: "--",
    utilityName: "--",
    utilityId: "--",
    scheduleId: "--",
    totalCost: 0,
  };

  const utilityDetails =
    savedReport?.utilityDetails ||
    (!viewingSavedReportOnly &&
      (incomingState.utilityDetails || defaultUtilityDetails)) ||
    undefined;

  const defaultEnergyCosts = {
    energy: Array(5).fill(0),
    energyTime: Array(5).fill(0),
    incrementalEnergy: Array(5).fill(0),
    demand: Array(5).fill(0),
    demandTime: Array(5).fill(0),
    incrementalDemand: Array(5).fill(0),
    serviceCharge: Array(5).fill(0),
    otherCharges: Array(5).fill(0),
    total: Array(5).fill(0),
  };

  const energyCostsView =
    savedReport?.energyCosts ||
    (!viewingSavedReportOnly && incomingState.energyCosts) ||
    (!viewingSavedReportOnly ? defaultEnergyCosts : undefined);
  const hasUtilityDetails = Boolean(utilityDetails);
  const hasEnergyCosts = Boolean(energyCostsView);

  const shouldShowUtilitySection =
    !(
      savedReport?.disableUtilityEnergyInfo ??
      incomingState.disableUtilityEnergyInfo ??
      false
    ) &&
    (hasUtilityDetails || hasEnergyCosts);

  useEffect(() => {
    const hasWLDownload = downloadFormat === "WL";
    if (hasWLDownload && !whiteLabel && !whiteLabelLoading) {
      dispatch(getMyAddWhiteLabel());
    }
  }, [dispatch, downloadFormat, whiteLabel, whiteLabelLoading]);

  useLayoutEffect(() => {
    requestAnimationFrame(() => {
      const scrollingElement =
        document.scrollingElement || document.documentElement;
      scrollingElement.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.body.scrollTop = 0;
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  }, [location.pathname]);

  const startEditing = () => {
    if (!savedReport?.financialTable || !savedReport._id) {
      setSnackbarMessage("Cannot edit: Report data incomplete");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    // Create a deep copy of the financial table for editing
    const tableCopy = savedReport.financialTable.map((item) => ({
      ...item,
      value: formatNegativeCategoryValue(
        item.category,
        typeof item.value === "string" ? item.value : String(item.value)
      ),
    }));

    setEditedTable(tableCopy);
    setIsEditing(true);

    // Initialize recalculated values
    const initialNetProfit = calculateNetProfitFromTable(tableCopy);
    setRecalculatedNetProfit(initialNetProfit);
  };

  const triggerRecalculation = (
    updatedTable: Array<{ category: string; value: string | number }>
  ) => {
    // Clear any existing timeout
    if (recalculationTimeout) {
      clearTimeout(recalculationTimeout);
    }

    setIsRecalculating(true);

    // Set new timeout for debouncing
    const timeout = setTimeout(() => {
      const newNetProfit = calculateNetProfitFromTable(updatedTable);
      setRecalculatedNetProfit(newNetProfit);
      setIsRecalculating(false);
    }, 800); // Increased delay for better debouncing

    setRecalculationTimeout(timeout);
  };

  const calculateNetProfitFromTable = (
    tableData: Array<{ category: string; value: string | number }>
  ) => {
    // Helper function to extract numeric value from table cell
    const extractNumericValue = (category: string): number => {
      const item = tableData.find((row) => row.category === category);
      if (!item) return 0;

      let value = item.value;

      // Handle currency format ($1,000.00)
      if (typeof value === "string") {
        // Remove currency symbols, commas, and any non-numeric characters except decimal point
        const numericString = value
          .replace(/[$,]/g, "")
          .replace(/[^\d.-]/g, "");
        const numValue = parseFloat(numericString);
        return isNaN(numValue) ? 0 : Math.abs(numValue);
      }

      return Math.abs(Number(value)) || 0;
    };

    // Extract all relevant values with proper category matching
    const chargingRev5 = extractNumericValue("Charger Revenue (5yrs)");
    const indirectRev5 = extractNumericValue("Indirect Revenue (5yrs)");
    const install = extractNumericValue("Install Cost");
    const hardware = extractNumericValue("Hardware Cost");
    const network = extractNumericValue("Network Cost");
    const maintenance = extractNumericValue("Maintenance");
    const utilityCost5 = extractNumericValue(
      "Utility Bill (Energy + Demand + Fixed)"
    );
    const grant = extractNumericValue("Grants/Subsidies & Tax Credit");

    // Extract tax savings if available
    const taxSavings = extractNumericValue(
      "Section 179 / 168(k) Tax Savings (Est.)"
    );

    // Calculate total revenue
    const totalRev5 = chargingRev5 + indirectRev5;

    // Calculate net profit using the standard formula
    const netProfit5 = Math.round(
      totalRev5 -
        install -
        hardware -
        network -
        maintenance -
        utilityCost5 +
        grant +
        taxSavings
    );

    // Update recalculated values for display
    setRecalculatedValues({
      install,
      hardware,
      network,
      maintenance,
      utilityCost5,
      grant,
      netProfit5,
    });

    return netProfit5;
  };

  // Add this useEffect for cleanup
  useEffect(() => {
    return () => {
      if (recalculationTimeout) {
        clearTimeout(recalculationTimeout);
      }
    };
  }, [recalculationTimeout]);

  const handleSave = async () => {
    if (!savedReport || !editedTable.length || !savedReport._id) return;

    try {
      // Format the table data for API - convert currency strings to numbers where appropriate
      const formattedTable = editedTable.map((item) => {
        let formattedValue = stripNegativePrefix(item.category, item.value);

        // If it's a currency value (starts with $), convert to number string
        if (
          typeof formattedValue === "string" &&
          formattedValue.startsWith("$")
        ) {
          const numericValue = formattedValue.replace(/[$,]/g, "");
          formattedValue = isNaN(Number(numericValue))
            ? formattedValue
            : numericValue;
        }

        return {
          category: item.category,
          value: formattedValue,
        };
      });

      // Calculate the new net profit based on edited values
      const newNetProfit =
        recalculatedNetProfit !== null
          ? recalculatedNetProfit
          : calculateNetProfitFromTable(editedTable);

      await dispatch(
        updateSiteReportData({
          id: savedReport._id!,
          netProfit: newNetProfit,
          financialTable: formattedTable,
        })
      ).unwrap();

      // Show success message
      setSnackbarMessage("Report updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      // Reset editing state
      setIsEditing(false);
      setRecalculatedNetProfit(null);
      setRecalculatedValues({});
      setIsRecalculating(false);

      if (recalculationTimeout) {
        clearTimeout(recalculationTimeout);
        setRecalculationTimeout(null);
      }
    } catch (err) {
      console.error("Failed to save financial table:", err);
      setSnackbarMessage("Failed to save report. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleCancel = () => {
    setEditedTable([]);
    setIsEditing(false);
    setRecalculatedNetProfit(null);
    setRecalculatedValues({});
    setIsRecalculating(false);

    if (recalculationTimeout) {
      clearTimeout(recalculationTimeout);
      setRecalculationTimeout(null);
    }
  };

  // Reusable plugin to add shadow to line charts (unchanged)
  const lineShadowPlugin = useMemo(
    () => ({
      id: "lineShadow",
      beforeDatasetsDraw: (chart: any, _args: any, pluginOptions: any) => {
        const ctx = chart.ctx as CanvasRenderingContext2D;
        ctx.save();
        ctx.shadowColor = pluginOptions?.color || "rgba(0,0,0,0.25)";
        ctx.shadowBlur = pluginOptions?.blur ?? 5;
        ctx.shadowOffsetX = pluginOptions?.offsetX ?? 5;
        ctx.shadowOffsetY = pluginOptions?.offsetY ?? 5;
      },
      beforeDatasetDraw: (chart: any, args: any, pluginOptions: any) => {
        const ctx = chart.ctx as CanvasRenderingContext2D;
        const dataset = chart.data?.datasets?.[args.index] as any;
        const baseAlpha = pluginOptions?.alpha ?? 10;

        const toRgba = (color: any, alpha: number) => {
          if (typeof color !== "string") return `rgba(0,0,0,${alpha})`;
          if (color.startsWith("#")) {
            let r = 0,
              g = 0,
              b = 0;
            if (color.length === 4) {
              r = parseInt(color[1] + color[1], 16);
              g = parseInt(color[2] + color[2], 16);
              b = parseInt(color[3] + color[3], 16);
            } else if (color.length === 7) {
              r = parseInt(color.slice(1, 3), 16);
              g = parseInt(color.slice(3, 5), 16);
              b = parseInt(color.slice(5, 7), 16);
            }
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
          }
          const rgbMatch = color.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i);
          if (rgbMatch) {
            const [, r, g, b] = rgbMatch;
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
          }
          const rgbaMatch = color.match(
            /^rgba\((\d+),\s*(\d+),\s*(\d+),\s*([0-9]*\.?[0-9]+)\)$/i
          );
          if (rgbaMatch) {
            const [, r, g, b] = rgbaMatch;
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
          }
          return color;
        };

        let borderColor = dataset?.borderColor;
        if (Array.isArray(borderColor) && borderColor.length > 0) {
          borderColor = borderColor[0];
        }
        ctx.shadowColor = toRgba(borderColor, baseAlpha);
      },
      afterDatasetsDraw: (chart: any) => {
        const ctx = chart.ctx as CanvasRenderingContext2D;
        ctx.restore();
      },
    }),
    []
  );

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleDownloadReport = async () => {
    if (!reportData && !savedReport) {
      alert("No report data to download.");
      return;
    }

    const isWhiteLabel = downloadFormat === "WL";
    let whiteLabelConfig: WhiteLabelConfig | undefined;

    if (isWhiteLabel) {
      if (!whiteLabel && !whiteLabelLoading) {
        await dispatch(getMyAddWhiteLabel());
      }

      if (
        whiteLabel?.brandLogo &&
        whiteLabel?.brandColor &&
        whiteLabel?.textColor &&
        whiteLabel?.bgColor &&
        whiteLabel?.domainURL &&
        whiteLabel?.termsOfServiceURL &&
        whiteLabel?.privacyPolicyURL
      ) {
        whiteLabelConfig = {
          brandLogo: whiteLabel.brandLogo,
          brandColor: whiteLabel.brandColor,
          textColor: whiteLabel.textColor,
          bgColor: whiteLabel.bgColor,
          domainURL: whiteLabel.domainURL || "",
          termsOfServiceURL: whiteLabel.termsOfServiceURL || "",
          privacyPolicyURL: whiteLabel.privacyPolicyURL || "",
        };
      }
    }

    try {
      setDownloading(true);
      const details: ReportDetails = {
        reportName:
          savedReport?.reportName || formData?.address || "Charging Station",
        netProfit: savedReport?.netProfit ?? reportData!.netProfit5,
        address: savedReport?.location || formData?.address || "",
        financialTable:
          savedReport?.financialTable && savedReport.financialTable.length > 0
            ? savedReport.financialTable
            : reportData
            ? [
                {
                  category: "Charger Revenue (5yrs)",
                  value: `$${reportData.chargingRev5.toLocaleString()}`,
                },
                {
                  category: "Added Dwell Time (per visit)",
                  value: reportData.addedDwell,
                },
                {
                  category: "Additional Spend (per visit)",
                  value: reportData.addSpend,
                },
                { category: "Traffic Increase", value: reportData.traffic },
                {
                  category: "Indirect Revenue (5yrs)",
                  value: `$${reportData.indirectRev5.toLocaleString()}`,
                },
                {
                  category: "Total Revenue",
                  value: `$${reportData.totalRev5.toLocaleString()}`,
                },
                {
                  category: "-Install Cost",
                  value: `$${reportData.install.toLocaleString()}`,
                },
                {
                  category: "-Hardware Cost",
                  value: `$${reportData.hardware.toLocaleString()}`,
                },
                {
                  category: "-Network Cost",
                  value: `$${reportData.network.toLocaleString()}`,
                },
                {
                  category: "-Maintenance",
                  value: `$${reportData.maintenance.toLocaleString()}`,
                },
                {
                  category: "-Utility Cost",
                  value: `$${reportData.utilityCost5.toLocaleString()}`,
                },
                {
                  category: "-Grants/Subsidies & Tax Credit",
                  value: `$${reportData.grant.toLocaleString()}`,
                },
                ...(reportData.applyTaxBenefits
                  ? [
                      {
                        category: "Section 179 / 168(k) Tax Savings (Est.)",
                        value: `$${Math.round(
                          reportData.taxSavings || 0
                        ).toLocaleString()}`,
                      },
                    ]
                  : []),
                ...(reportData.customerSplit &&
                reportData.softwareProviderSplit &&
                reportData.operatorSplit
                  ? [
                      {
                        category: `Customer Share (${reportData.customerSplit}%)`,
                        value: `$${Math.round(
                          reportData.customerProfit || 0
                        ).toLocaleString()}`,
                      },
                      {
                        category: `Software Provider Share (${reportData.softwareProviderSplit}%)`,
                        value: `$${Math.round(
                          reportData.softwareProviderProfit || 0
                        ).toLocaleString()}`,
                      },
                      {
                        category: `Operator Share (${reportData.operatorSplit}%)`,
                        value: `$${Math.round(
                          reportData.operatorProfit || 0
                        ).toLocaleString()}`,
                      },
                    ]
                  : []),
              ]
            : [],
        breakEvenCashFlow: savedReport?.breakEvenCashFlow || {
          labels: ["0", "1", "2", "3", "4", "5"],
          data: (() => {
            const cash = [
              -(
                reportData!.install +
                reportData!.hardware +
                reportData!.network
              ),
            ];
            for (let i = 0; i < 5; i++) {
              const yVal =
                reportData!.chargingRevArr[i] +
                reportData!.indirectArr[i] -
                reportData!.maintenance / 5 -
                reportData!.kWhYearArr[i] * utilityRate;
              cash.push(cash[i] + yVal);
            }
            return cash;
          })(),
        },
        revenueCost: savedReport?.revenueCost || {
          labels: ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"],
          datasets: [
            {
              label: "Charging Rev",
              data: reportData!.chargingRevArr,
            },
            {
              label: "Indirect Rev",
              data: reportData!.indirectArr,
            },
            {
              label: "Install Cost",
              data: [reportData!.install, 0, 0, 0, 0],
            },
            {
              label: "Hardware Cost",
              data: [reportData!.hardware, 0, 0, 0, 0],
            },
            {
              label: "Network Cost",
              data: [reportData!.network, 0, 0, 0, 0],
            },
            {
              label: "Maintenance",
              data: Array(5).fill(reportData!.maintenance / 5),
            },
            ...(energyCostsView
              ? [
                  {
                    label: "Utility Cost",
                    data: energyCostsView.total,
                  },
                ]
              : []),
            ...(energyCostsView
              ? [
                  {
                    label: "Utility Cost",
                    data: energyCostsView.total,
                  },
                ]
              : []),
          ],
        },
        co2Savings: savedReport?.co2Savings || {
          labels: ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"],
          datasets: [
            {
              label: "CO₂ Offset (kg)",
              data: reportData!.co2Arr.map((x) => Math.round(x)),
            },
            {
              label: "Gasoline Replaced (gal)",
              data: reportData!.gasArr.map((x) => Math.round(x)),
            },
          ],
        },
        utilityDetails: savedReport?.utilityDetails ||
          incomingState.utilityDetails || {
            state: "--",
            zipCode: "--",
            utilityName: "--",
            utilityId: "--",
            scheduleId: "--",
            totalCost: 0,
          },
        energyCosts: savedReport?.energyCosts ||
          incomingState.energyCosts || {
            energy: Array(5).fill(0),
            energyTime: Array(5).fill(0),
            incrementalEnergy: Array(5).fill(0),
            demand: Array(5).fill(0),
            demandTime: Array(5).fill(0),
            incrementalDemand: Array(5).fill(0),
            serviceCharge: Array(5).fill(0),
            otherCharges: Array(5).fill(0),
            total: Array(5).fill(0),
          },
        grantCostDetails:
          savedReport?.grantCostDetails || incomingState.grantCostDetails || [],
        excludeIncentives:
          savedReport?.excludeIncentives ||
          incomingState.excludeIncentives ||
          false,
        taxBenefits: {
          applyTaxBenefits:
            savedReport?.applyTaxBenefits || incomingState.applyTaxBenefits,
          businessTaxRate:
            savedReport?.businessTaxRate || incomingState.businessTaxRate,
          section179Limit:
            savedReport?.section179Limit || incomingState.section179Limit,
          bonusDepreciationPct:
            savedReport?.bonusDepreciationPct ||
            incomingState.bonusDepreciationPct,
          qualifyingSpend:
            savedReport?.qualifyingSpend || incomingState.qualifyingSpend,
          taxSavings: savedReport?.taxSavings || incomingState.taxSavings,
          upfrontAfterTax:
            savedReport?.upfrontAfterTax || incomingState.upfrontAfterTax,
        },
      };

      await downloadReport(details, isWhiteLabel, whiteLabelConfig, false);
    } catch (err) {
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    let map: google.maps.Map | null = null;
    let panorama: google.maps.StreetViewPanorama | null = null;
    let marker: google.maps.Marker | null = null;
    let infoWindow: google.maps.InfoWindow | null = null;

    const initMap = () => {
      if (!mapRef.current || !window.google) return;

      const defaultPosition = new window.google.maps.LatLng(
        40.518715,
        -74.412095
      );
      const address =
        savedReport?.location ||
        formData?.address ||
        "981 US Route 1, Edison, NJ, USA";

      // Initialize the map
      map = new window.google.maps.Map(mapRef.current, {
        center: defaultPosition,
        zoom: 15,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        mapTypeControl: false,
        streetViewControl: false,
      });

      // Initialize the Street View panorama
      panorama = new window.google.maps.StreetViewPanorama(mapRef.current, {
        position: defaultPosition,
        pov: {
          heading: 165,
          pitch: 0,
        },
        zoom: 1,
        addressControl: true,
        showRoadLabels: true,
        visible: mapView === "streetview",
        motionTracking: false,
        linksControl: true,
        panControl: true,
        enableCloseButton: false,
      });

      // Set up Street View service and geocoder
      const streetViewService = new window.google.maps.StreetViewService();
      const geocoder = new window.google.maps.Geocoder();

      // Function to update map view
      const updateMapView = (
        view: "streetview" | "roadmap" | "satellite",
        position: google.maps.LatLng
      ) => {
        if (!mapRef.current || !map || !panorama) return;

        // Update map or panorama based on view
        if (view === "streetview") {
          mapRef.current.innerHTML = "";
          panorama = new window.google.maps.StreetViewPanorama(mapRef.current, {
            position: position,
            pov: panorama.getPov(),
            zoom: panorama.getZoom(),
            addressControl: true,
            showRoadLabels: true,
            visible: true,
            motionTracking: false,
            linksControl: true,
            panControl: true,
            enableCloseButton: false,
          });
          map.setStreetView(panorama);
        } else {
          mapRef.current.innerHTML = "";
          panorama.setVisible(false);
          map = new window.google.maps.Map(mapRef.current, {
            center: position,
            zoom: 15,
            mapTypeId:
              view === "satellite"
                ? window.google.maps.MapTypeId.SATELLITE
                : window.google.maps.MapTypeId.ROADMAP,
            mapTypeControl: false,
            streetViewControl: false,
          });
        }

        // Update or create marker
        if (marker) {
          marker.setMap(null);
          marker.setPosition(position);
        } else {
          marker = new window.google.maps.Marker({
            position: position,
            title: "Charging Station",
            icon: {
              url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
              scaledSize: new window.google.maps.Size(32, 32),
            },
          });
        }

        // Update or create InfoWindow
        if (infoWindow) {
          infoWindow.close();
          infoWindow.setContent(
            `<b>Location:</b> ${address}<br><b>Charging Station Site${
              view === "streetview"
                ? ""
                : view === "satellite"
                ? " (Satellite View)"
                : " (Map View)"
            }</b>`
          );
        } else {
          infoWindow = new window.google.maps.InfoWindow({
            content: `<b>Location:</b> ${address}<br><b>Charging Station Site${
              view === "streetview"
                ? ""
                : view === "satellite"
                ? " (Satellite View)"
                : " (Map View)"
            }</b>`,
          });
        }

        if (view !== "streetview") {
          marker.setMap(map);
          infoWindow.open(map, marker);
        } else {
          marker.setMap(null);
          infoWindow.close();
        }
      };

      // Geocode the address
      geocoder.geocode(
        { address },
        (
          results: google.maps.GeocoderResult[] | null,
          status: google.maps.GeocoderStatus
        ) => {
          if (
            status === window.google.maps.GeocoderStatus.OK &&
            results &&
            results[0]
          ) {
            const pos = results[0].geometry.location;
            setPosition(pos);

            // Check Street View availability
            streetViewService.getPanorama(
              {
                location: pos,
                radius: 50,
              },
              (
                data: google.maps.StreetViewPanoramaData | null,
                svStatus: google.maps.StreetViewStatus
              ) => {
                const available =
                  svStatus === window.google.maps.StreetViewStatus.OK;
                setStreetViewAvailable(available);
                if (!available && mapView === "streetview") {
                  setMapView("roadmap");
                  setSnackbarMessage(
                    "Street View is not available for this location."
                  );
                  setSnackbarSeverity("warning");
                  setSnackbarOpen(true);
                }
                updateMapView(mapView, pos);
                setMapLoaded(true);
              }
            );
          } else {
            // Geocoding failed, use default position
            setPosition(defaultPosition);
            updateMapView(mapView, defaultPosition);
            setMapLoaded(true);
          }
        }
      );
    };

    const loadScript = () => {
      if (window.google && window.google.maps) {
        initMap();
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDZsmG9zzmlEZLY3BpPYeJMfUphIobw-sM&libraries=places`;
      script.async = true;
      script.onload = initMap;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    };

    loadScript();

    // Cleanup
    return () => {
      if (marker) marker.setMap(null);
      if (infoWindow) infoWindow.close();
      if (map) {
        google.maps.event.clearInstanceListeners(map);
      }
      if (panorama) {
        google.maps.event.clearInstanceListeners(panorama);
      }
    };
  }, [savedReport, formData, mapView]);

  // Chart initialization (unchanged)
  useEffect(() => {
    // Break Even Chart
    const breakEvenCtx = breakEvenChartRef.current?.getContext("2d");
    if (breakEvenCtx && (savedReport || reportData)) {
      if (breakEvenChartInstance.current)
        breakEvenChartInstance.current.destroy();
      const labels = savedReport?.breakEvenCashFlow?.labels || [
        "0",
        "1",
        "2",
        "3",
        "4",
        "5",
      ];
      const data =
        savedReport?.breakEvenCashFlow?.data ||
        (() => {
          // Use after-tax upfront cost if tax benefits are applied
          const upfrontCost = reportData!.applyTaxBenefits
            ? -(reportData!.upfrontAfterTax || 0)
            : -(
                reportData!.install +
                reportData!.hardware +
                reportData!.network
              );

          const cash = [upfrontCost];
          for (let i = 0; i < 5; i++) {
            const yVal =
              reportData!.chargingRevArr[i] +
              reportData!.indirectArr[i] -
              reportData!.maintenance / 5 -
              reportData!.kWhYearArr[i] * utilityRate;
            cash.push(cash[i] + yVal);
          }
          return cash;
        })();
      breakEvenChartInstance.current = new Chart(breakEvenCtx, {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: "Cumulative Cash Flow",
              data,
              borderColor: "#4b5b5d",
              backgroundColor: "#c3d5d6",
              fill: true,
              tension: 0.5,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              title: { display: true, text: "Cash ($)" },
              grid: {
                color: (ctx) =>
                  ctx.tick.value === 0 ? "#000" : "rgba(0,0,0,0.1)",
                lineWidth: (ctx) => (ctx.tick.value === 0 ? 1 : 1),
              },
            },
            x: {
              title: {
                display: true,
                text: "Years",
              },
            },
          },
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                font: {
                  size: isMobile ? 7 : 10,
                },
              },
            },
          },
        },
      });
    }

    // Revenue & Cost Chart
    const revenueCostCtx = revenueCostChartRef.current?.getContext("2d");
    if (revenueCostCtx && (savedReport || reportData)) {
      if (revenueCostChartInstance.current)
        revenueCostChartInstance.current.destroy();
      const labels = savedReport?.revenueCost?.labels || [
        "Year 1",
        "Year 2",
        "Year 3",
        "Year 4",
        "Year 5",
      ];
      const datasets = savedReport?.revenueCost?.datasets
        ? savedReport.revenueCost.datasets.map(
            (dataset: any, index: number) => ({
              ...dataset,
              borderColor:
                [
                  "#4B5B5D",
                  "#4682B4",
                  "#B22222",
                  "#A9A9A9",
                  "#3CB371",
                  "#FFA500",
                  "#D3D3D3",
                ][index] || "#4B5B5D",
              backgroundColor: "rgba(75, 91, 93, 0.2)",
              fill: false,
              tension: 1,
            })
          )
        : [
            {
              label: "Charging Rev",
              data: reportData!.chargingRevArr,
              borderColor: "#4B5B5D",
              backgroundColor: "rgba(75, 91, 93, 0.2)",
              fill: false,
              tension: 1,
            },
            {
              label: "Indirect Rev",
              data: reportData!.indirectArr,
              borderColor: "#4682B4",
              backgroundColor: "rgba(70, 130, 180, 0.2)",
              fill: false,
              tension: 1,
            },
            {
              label: "Install Cost",
              data: [reportData!.install, 0, 0, 0, 0],
              borderColor: "#B22222",
              backgroundColor: "rgba(178, 34, 34, 0.2)",
              fill: false,
              tension: 1,
            },
            {
              label: "Hardware Cost",
              data: [reportData!.hardware, 0, 0, 0, 0],
              borderColor: "#A9A9A9",
              backgroundColor: "rgba(169, 169, 169, 0.2)",
              fill: false,
              tension: 1,
            },
            {
              label: "Network Cost",
              data: [reportData!.network, 0, 0, 0, 0],
              borderColor: "#3CB371",
              backgroundColor: "rgba(60, 179, 113, 0.2)",
              fill: false,
              tension: 1,
            },
            {
              label: "Maintenance",
              data: Array(5).fill(reportData!.maintenance / 5),
              borderColor: "#FFA500",
              backgroundColor: "rgba(255, 165, 0, 0.2)",
              fill: false,
              tension: 1,
            },
            ...(energyCostsView
              ? [
                  {
                    label: "Utility Cost",
                    data: energyCostsView.total,
                    borderColor: "#D3D3D3",
                    backgroundColor: "rgba(211, 211, 211, 0.2)",
                    fill: false,
                    tension: 1,
                  },
                ]
              : []),
          ];
      revenueCostChartInstance.current = new Chart(revenueCostCtx, {
        type: "line",
        data: {
          labels,
          datasets: datasets as any,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              type: "linear",
              title: { display: true, text: "USD ($)" },
              min: 0,
            },
            x: {
              title: {
                display: true,
                text: "Years",
              },
            },
          },
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                font: {
                  size: isMobile ? 7 : 10,
                },
              },
            },
            tooltip: {
              callbacks: {
                label: (ctx: any) => {
                  const label = ctx.dataset.label || "";
                  const value = ctx.parsed.y ?? ctx.parsed;
                  return `${label}: $${Number(value).toLocaleString()}`;
                },
              },
            },
          },
        },
        plugins: [lineShadowPlugin],
      });
    }

    // CO2 Savings Chart
    const co2SavingsCtx = co2SavingsChartRef.current?.getContext("2d");
    if (co2SavingsCtx && (savedReport || reportData)) {
      if (co2SavingsChartInstance.current)
        co2SavingsChartInstance.current.destroy();

      // Fallback-safe labels
      const labels = savedReport?.co2Savings?.labels || [
        "Year 1",
        "Year 2",
        "Year 3",
        "Year 4",
        "Year 5",
      ];

      // Choose data source safely
      const co2Data =
        savedReport?.co2Savings?.datasets?.[0]?.data ??
        reportData?.co2Arr?.map((x) => Math.round(x)) ??
        Array(5).fill(0);
      const gasData =
        savedReport?.co2Savings?.datasets?.[1]?.data ??
        reportData?.gasArr?.map((x) => Math.round(x)) ??
        Array(5).fill(0);

      co2SavingsChartInstance.current = new Chart(co2SavingsCtx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "CO₂ Offset (kg)",
              data: co2Data,
              yAxisID: "y1",
              backgroundColor: "#455354",
              borderColor: "#2d393a",
              borderWidth: 1,
              borderSkipped: false,
              barPercentage: 0.35,
              categoryPercentage: 0.6,
            },
            {
              label: "Gasoline Replaced (gal)",
              data: gasData,
              yAxisID: "y2",
              backgroundColor: "#91a5a6",
              borderColor: "#758d8e",
              borderWidth: 1,
              borderSkipped: false,
              barPercentage: 0.35,
              categoryPercentage: 0.6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: "index", intersect: false },
          scales: {
            y1: {
              beginAtZero: true,
              position: "left",
              title: { display: true, text: "CO₂ Offset (kg)" },
              grid: { color: "rgba(0,0,0,0.1)" },
              ticks: { callback: (v) => v.toLocaleString() },
            },
            y2: {
              beginAtZero: true,
              position: "right",
              title: { display: true, text: "Gasoline Replaced (gal)" },
              grid: { drawOnChartArea: false },
              ticks: { callback: (v) => v.toLocaleString() },
            },
            x: {
              title: { display: true, text: "Years" },
              stacked: false,
            },
          },
          plugins: {
            legend: {
              position: "bottom",
              labels: { font: { size: isMobile ? 7 : 10 } },
            },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const label = ctx.dataset.label || "";
                  const val = ctx.parsed.y ?? 0;
                  return `${label}: ${Number(val).toLocaleString()}`;
                },
              },
            },
          },
        },
      });
    }

    return () => {
      breakEvenChartInstance.current?.destroy();
      revenueCostChartInstance.current?.destroy();
      co2SavingsChartInstance.current?.destroy();
    };
  }, [
    reportData,
    savedReport,
    utilityRate,
    isMobile,
    lineShadowPlugin,
    energyCostsView,
  ]);

  const handleMapViewChange = (e: any) => {
    const newView = e.target.value;
    if (newView === "streetview" && !streetViewAvailable) {
      setSnackbarMessage("Street View is not available for this location.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    setMapView(newView);
  };

  const baseReportTableData =
    savedReport?.financialTable && savedReport.financialTable.length > 0
      ? savedReport.financialTable
      : reportData
      ? [
          {
            category: "Charger Revenue (5yrs)",
            value: `$${reportData.chargingRev5.toLocaleString()}`,
          },
          {
            category: "Added Dwell Time (per visit)",
            value: reportData.addedDwell,
          },
          {
            category: "Additional Spend (per visit)",
            value: reportData.addSpend,
          },
          { category: "Traffic Increase", value: reportData.traffic },
          {
            category: "Indirect Revenue (5yrs)",
            value: `$${reportData.indirectRev5.toLocaleString()}`,
          },
          {
            category: "Total Revenue",
            value: `$${reportData.totalRev5.toLocaleString()}`,
          },
          {
            category: "-Install Cost",
            value: `$${reportData.install.toLocaleString()}`,
          },
          {
            category: "-Hardware Cost",
            value: `$${reportData.hardware.toLocaleString()}`,
          },
          {
            category: "-Network Cost",
            value: `$${reportData.network.toLocaleString()}`,
          },
          {
            category: "-Maintenance",
            value: `$${reportData.maintenance.toLocaleString()}`,
          },
          {
            category: "-Utility Cost",
            value: `$${reportData.utilityCost5.toLocaleString()}`,
          },
          {
            category: "-Grants/Subsidies & Tax Credit",
            value: `$${reportData.grant.toLocaleString()}`,
          },
          ...(reportData.applyTaxBenefits || incomingState.applyTaxBenefits
            ? [
                {
                  category: "Section 179 / 168(k) Tax Savings (Est.)",
                  value: `$${Math.round(
                    reportData.taxSavings || incomingState.taxSavings || 0
                  ).toLocaleString()}`,
                },
              ]
            : []),
          ...((reportData.customerSplit !== undefined &&
            reportData.softwareProviderSplit !== undefined &&
            reportData.operatorSplit !== undefined) ||
          (incomingState.customerSplit !== undefined &&
            incomingState.softwareProviderSplit !== undefined &&
            incomingState.operatorSplit !== undefined)
            ? [
                {
                  category: `Customer Share (${
                    reportData.customerSplit || incomingState.customerSplit
                  }%)`,
                  value: `$${Math.round(
                    reportData.customerProfit ||
                      incomingState.customerProfit ||
                      0
                  ).toLocaleString()}`,
                },
                {
                  category: `Software Provider Share (${
                    reportData.softwareProviderSplit ||
                    incomingState.softwareProviderSplit
                  }%)`,
                  value: `$${Math.round(
                    reportData.softwareProviderProfit ||
                      incomingState.softwareProviderProfit ||
                      0
                  ).toLocaleString()}`,
                },
                {
                  category: `Operator Share (${
                    reportData.operatorSplit || incomingState.operatorSplit
                  }%)`,
                  value: `$${Math.round(
                    reportData.operatorProfit ||
                      incomingState.operatorProfit ||
                      0
                  ).toLocaleString()}`,
                },
              ]
            : []),
        ]
      : [];

  const reportTableData = baseReportTableData.map((item) => ({
    ...item,
    value: formatNegativeCategoryValue(item.category, item.value),
  }));

  // Use precomputed utilityDetails and energyCosts
  const grantCostDetails =
    savedReport?.grantCostDetails || incomingState.grantCostDetails || [];
  const shouldShowGrantSection =
    !savedReport?.excludeIncentives &&
    !incomingState.excludeIncentives &&
    (viewingSavedReportOnly ? grantCostDetails.length > 0 : true);

  const shouldShowTaxBenefits = viewingSavedReportOnly
    ? !!savedReport?.applyTaxBenefits
    : !!(savedReport?.applyTaxBenefits || incomingState.applyTaxBenefits);

  // Calculate bullet point data
  const breakEvenData =
    savedReport?.breakEvenCashFlow?.data ||
    (reportData &&
      (() => {
        // Use after-tax upfront cost if tax benefits are applied
        const applyTaxBenefits =
          reportData.applyTaxBenefits || incomingState.applyTaxBenefits;
        const upfrontCost = applyTaxBenefits
          ? -(reportData.upfrontAfterTax || incomingState.upfrontAfterTax || 0)
          : -(reportData.install + reportData.hardware + reportData.network);

        const cash = [upfrontCost];
        for (let i = 0; i < 5; i++) {
          const yVal =
            reportData.chargingRevArr[i] +
            reportData.indirectArr[i] -
            reportData.maintenance / 5 -
            reportData.kWhYearArr[i] * utilityRate;
          cash.push(cash[i] + yVal);
        }
        return cash;
      })()) ||
    [];

  const breakEvenPoint = breakEvenData.findIndex((value) => value > 0);
  const breakEvenMonths = breakEvenPoint >= 0 ? breakEvenPoint * 12 : null;

  const co2SavingsData =
    savedReport?.co2Savings?.datasets ||
    (reportData && [
      {
        label: "CO₂ Offset (kg)",
        data: reportData.co2Arr.map((x) => Math.round(x)),
      },
      {
        label: "Gasoline Replaced (gal)",
        data: reportData.gasArr.map((x) => Math.round(x)),
      },
    ]) ||
    [];

  const totalCo2Offset =
    co2SavingsData[0]?.data?.reduce(
      (sum: number, value: number) => sum + value,
      0
    ) || 0;
  const totalGasolineSaved =
    co2SavingsData[1]?.data?.reduce(
      (sum: number, value: number) => sum + value,
      0
    ) || 0;

  const handleRowToggle = (index: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <Container>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Content>
          <HeaderContainer>
            <WelcomeText>L2 Charging Station Report</WelcomeText>
            <ActionsContainer>
              {/* Report Actions */}
              {!!savedReport && (
                <>
                  {isEditing ? (
                    <>
                      <Tooltip title="Save Changes">
                        <MuiIconButton
                          onClick={handleSave}
                          sx={{ color: "#4b5b5d" }}
                        >
                          <SaveIcon />
                        </MuiIconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <MuiIconButton
                          onClick={handleCancel}
                          sx={{ color: "#f54242" }}
                        >
                          <CancelIcon />
                        </MuiIconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <Tooltip title="Edit Report Details">
                      <MuiIconButton
                        onClick={startEditing}
                        sx={{ color: "#4b5b5d" }}
                      >
                        <EditIcon />
                      </MuiIconButton>
                    </Tooltip>
                  )}
                </>
              )}

              {/* Download Format */}
              <FormControl sx={{ minWidth: 50 }} size="small">
                <InputLabel id="download-format-label">Format</InputLabel>
                <Select
                  labelId="download-format-label"
                  value={downloadFormat}
                  label="Format"
                  onChange={(e) =>
                    setDownloadFormat(e.target.value as "PDF" | "WL")
                  }
                >
                  <MenuItem value="PDF">PDF</MenuItem>
                  {!isStarterPlan && <MenuItem value="WL">WL</MenuItem>}
                </Select>
              </FormControl>

              {/* Download Button */}
              <Tooltip title="Download Report">
                <StyledButton
                  onClick={handleDownloadReport}
                  startIcon={
                    downloading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <GetApp />
                    )
                  }
                  disabled={downloading || whiteLabelLoading}
                >
                  {downloading ? "Downloading..." : "Download"}
                </StyledButton>
              </Tooltip>

              {/* Back Button */}
              <Tooltip title="Back to Generate Report">
                <StyledBackButton aria-label="back" onClick={handleBack}>
                  <ArrowBack />
                </StyledBackButton>
              </Tooltip>
            </ActionsContainer>
          </HeaderContainer>
          {savedReport || reportData ? (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <ProfitContent>
                  <Box>
                    <SubTitleText>5-Year Summary</SubTitleText>
                    <TitleText>
                      {isRecalculating ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <CircularProgress
                            size={20}
                            sx={{ color: "#e8e5dc" }}
                          />
                          <Typography
                            component="span"
                            sx={{ color: "#e8e5dc" }}
                          >
                            Calculating...
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          $
                          {Math.round(
                            recalculatedNetProfit !== null
                              ? recalculatedNetProfit
                              : savedReport?.netProfit ?? reportData!.netProfit5
                          ).toLocaleString()}
                        </>
                      )}
                    </TitleText>
                    <BulletList>
                      {breakEvenMonths !== null && (
                        <li>{breakEvenMonths} Month Break Even Point</li>
                      )}
                      {totalCo2Offset > 0 && (
                        <li>{totalCo2Offset.toLocaleString()} kg CO₂ Offset</li>
                      )}
                      {totalGasolineSaved > 0 && (
                        <li>
                          {totalGasolineSaved.toLocaleString()} gallons of
                          Gasoline Saved
                        </li>
                      )}
                    </BulletList>
                  </Box>
                  {!isMobile && (
                    <MoneyTreeImage src={MoneyTree} alt="Money Tree" />
                  )}
                </ProfitContent>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Card>
                  <Title>Financial Breakdown</Title>
                  <Box sx={{ overflowX: "auto" }}>
                    <Table>
                      <TableHead>
                        <TableRow
                          style={{
                            background:
                              "linear-gradient(to left, #f4efe6, #bcc4c4)",
                          }}
                        >
                          <StyledTableCell>Category</StyledTableCell>
                          <StyledTableDataCell>
                            Amount/Value
                          </StyledTableDataCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportTableData.map((item, index) => {
                          const isEditableRow = isEditing && !!savedReport;

                          // Define which categories should be read-only (calculated values)
                          const readOnlyCategories = [
                            "Charger Revenue (5yrs)",
                            "Indirect Revenue (5yrs)",
                            "Added Dwell Time (per visit)",
                            "Additional Spend (per visit)",
                            "Traffic Increase",
                            "Total Revenue",
                            "Section 179 / 168(k) Tax Savings (Est.)",
                            "Customer Share (",
                            "Software Provider Share (",
                            "Operator Share (",
                            "Net Profit",
                          ];

                          const isReadOnly = readOnlyCategories.some(
                            (readOnlyCategory) =>
                              item.category.startsWith(readOnlyCategory)
                          );

                          return (
                            <StyledTableRow key={index}>
                              <StyledTableDataCell>
                                {item.category}
                              </StyledTableDataCell>
                              <EditableCell
                                value={
                                  isEditableRow
                                    ? editedTable[index]?.value ?? item.value
                                    : item.value
                                }
                                onChange={(newVal) => {
                                  const copy = [...editedTable];
                                  copy[index] = {
                                    ...copy[index],
                                    value: newVal,
                                  };
                                  setEditedTable(copy);
                                  triggerRecalculation(copy);
                                }}
                                disabled={!isEditableRow}
                                readOnly={isReadOnly}
                              />
                            </StyledTableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                </Card>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Card>
                  <Title>Utility Details & Energy Costs</Title>
                  {hasUtilityDetails && (
                    <Box sx={{ overflowX: "auto" }}>
                      <Table>
                        <TableHead>
                          <TableRow
                            style={{
                              background:
                                "linear-gradient(to left, #f4efe6, #bcc4c4)",
                            }}
                          >
                            <StyledTableCell>State</StyledTableCell>
                            <StyledTableCell>Zip Code</StyledTableCell>
                            <StyledTableCell>Utility Name</StyledTableCell>
                            <StyledTableCell>Utility ID</StyledTableCell>
                            <StyledTableCell>Schedule ID</StyledTableCell>
                            <StyledTableCell>Full Utility Bill</StyledTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <StyledTableRow>
                            <StyledTableDataCell>
                              {utilityDetails?.state ?? "--"}
                            </StyledTableDataCell>
                            <StyledTableDataCell>
                              {utilityDetails?.zipCode ?? "--"}
                            </StyledTableDataCell>
                            <StyledTableDataCell>
                              {utilityDetails?.utilityName ?? "--"}
                            </StyledTableDataCell>
                            <StyledTableDataCell>
                              {utilityDetails?.utilityId ?? "--"}
                            </StyledTableDataCell>
                            <StyledTableDataCell>
                              {utilityDetails?.scheduleId ?? "--"}
                            </StyledTableDataCell>
                            <StyledTableDataCell>
                              $
                              {Number(
                                utilityDetails?.totalCost ?? 0
                              ).toLocaleString()}
                            </StyledTableDataCell>
                          </StyledTableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                  {hasEnergyCosts && (
                    <Box
                      sx={{
                        overflowX: "auto",
                        mt: hasUtilityDetails ? 2 : 0,
                      }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow
                            style={{
                              background:
                                "linear-gradient(to left, #f4efe6, #bcc4c4)",
                            }}
                          >
                            <StyledTableCell>Category</StyledTableCell>
                            <StyledTableCell>Year 1</StyledTableCell>
                            <StyledTableCell>Year 2</StyledTableCell>
                            <StyledTableCell>Year 3</StyledTableCell>
                            <StyledTableCell>Year 4</StyledTableCell>
                            <StyledTableCell>Year 5</StyledTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <StyledTableRow>
                            <StyledTableDataCell>Energy</StyledTableDataCell>
                            {(energyCostsView?.energy ?? []).map(
                              (cost, index) => (
                                <StyledTableDataCell key={`energy-${index}`}>
                                  $
                                  {cost.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </StyledTableDataCell>
                              )
                            )}
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableDataCell>
                              Energy Time
                            </StyledTableDataCell>
                            {(energyCostsView?.energyTime ?? []).map(
                              (cost, index) => (
                                <StyledTableDataCell
                                  key={`energyTime-${index}`}
                                >
                                  $
                                  {cost.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </StyledTableDataCell>
                              )
                            )}
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableDataCell>
                              Incremental Energy
                            </StyledTableDataCell>
                            {(energyCostsView?.incrementalEnergy ?? []).map(
                              (cost, index) => (
                                <StyledTableDataCell key={`incEnergy-${index}`}>
                                  $
                                  {cost.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </StyledTableDataCell>
                              )
                            )}
                          </StyledTableRow>
                          {shouldShowUtilitySection && (
                            <StyledTableRow>
                              <StyledTableDataCell>Demand</StyledTableDataCell>
                              {(energyCostsView?.demand ?? []).map(
                                (cost, index) => (
                                  <StyledTableDataCell key={`demand-${index}`}>
                                    $
                                    {cost.toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </StyledTableDataCell>
                                )
                              )}
                            </StyledTableRow>
                          )}
                          <StyledTableRow>
                            <StyledTableDataCell>
                              Demand Time
                            </StyledTableDataCell>
                            {(energyCostsView?.demandTime ?? []).map(
                              (cost, index) => (
                                <StyledTableDataCell
                                  key={`demandTime-${index}`}
                                >
                                  $
                                  {cost.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </StyledTableDataCell>
                              )
                            )}
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableDataCell>
                              Incremental Demand
                            </StyledTableDataCell>
                            {(energyCostsView?.incrementalDemand ?? []).map(
                              (cost, index) => (
                                <StyledTableDataCell key={`incDemand-${index}`}>
                                  $
                                  {cost.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </StyledTableDataCell>
                              )
                            )}
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableDataCell>
                              Service Charge
                            </StyledTableDataCell>
                            {(energyCostsView?.serviceCharge ?? []).map(
                              (cost, index) => {
                                const displayCost = cost > 500 ? 500 : cost;
                                return (
                                  <StyledTableDataCell key={`service-${index}`}>
                                    $
                                    {displayCost.toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                    {cost > 500 && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "#ff6b35",
                                          ml: 0.5,
                                          fontSize: "0.6rem",
                                        }}
                                      >
                                        *
                                      </Typography>
                                    )}
                                  </StyledTableDataCell>
                                );
                              }
                            )}
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableDataCell>
                              Other Charges
                            </StyledTableDataCell>
                            {(energyCostsView?.otherCharges ?? []).map(
                              (cost, index) => (
                                <StyledTableDataCell key={`other-${index}`}>
                                  $
                                  {cost.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </StyledTableDataCell>
                              )
                            )}
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableDataCell>Total</StyledTableDataCell>
                            {(energyCostsView?.total ?? []).map((_, index) => {
                              const energy =
                                energyCostsView?.energy?.[index] || 0;
                              const energyTime =
                                energyCostsView?.energyTime?.[index] || 0;
                              const incrementalEnergy =
                                energyCostsView?.incrementalEnergy?.[index] ||
                                0;
                              const demand =
                                energyCostsView?.demand?.[index] || 0;
                              const demandTime =
                                energyCostsView?.demandTime?.[index] || 0;
                              const incrementalDemand =
                                energyCostsView?.incrementalDemand?.[index] ||
                                0;
                              const serviceCharge =
                                energyCostsView?.serviceCharge?.[index] || 0;
                              const otherCharges =
                                energyCostsView?.otherCharges?.[index] || 0;

                              // Apply cap to service charge
                              const cappedServiceCharge =
                                serviceCharge > 500 ? 500 : serviceCharge;

                              // Conditionally include demand based on shouldShowUtilitySection
                              const demandValue = shouldShowUtilitySection
                                ? demand
                                : 0;

                              // Sum all components with capped service charge and conditional demand
                              const recalculatedTotal =
                                energy +
                                energyTime +
                                incrementalEnergy +
                                demandValue + // Use conditional demand value
                                demandTime +
                                incrementalDemand +
                                cappedServiceCharge +
                                otherCharges;

                              return (
                                <StyledTableDataCell key={`total-${index}`}>
                                  $
                                  {recalculatedTotal.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                  {serviceCharge > 500 && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#ff6b35",
                                        ml: 0.5,
                                        fontSize: "0.6rem",
                                      }}
                                    >
                                      *
                                    </Typography>
                                  )}
                                </StyledTableDataCell>
                              );
                            })}
                          </StyledTableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </Card>
              </Grid>

              {shouldShowGrantSection && (
                <Grid size={{ xs: 12 }}>
                  <Card>
                    <Title>Grant Cost Details</Title>
                    <Box sx={{ overflowX: "auto" }}>
                      <Table>
                        <TableHead>
                          <TableRow
                            style={{
                              background:
                                "linear-gradient(to left, #f4efe6, #bcc4c4)",
                            }}
                          >
                            <StyledTableCell />
                            <StyledTableCell>Program Name</StyledTableCell>
                            <StyledTableCell>Credit Amount</StyledTableCell>
                            <StyledTableCell>Applies To</StyledTableCell>
                            <StyledTableCell>Eligibility</StyledTableCell>
                            <StyledTableCell>Valid Until</StyledTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {grantCostDetails.length > 0 ? (
                            grantCostDetails.map((grant, index) => (
                              <React.Fragment key={index}>
                                <StyledTableRow>
                                  <StyledTableDataCell>
                                    <MuiIconButton
                                      aria-label={
                                        expandedRows[index]
                                          ? "Collapse"
                                          : "Expand"
                                      }
                                      onClick={() => handleRowToggle(index)}
                                      size="small"
                                    >
                                      <ExpandMoreIcon
                                        sx={{
                                          transform: expandedRows[index]
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                        }}
                                      />
                                    </MuiIconButton>
                                  </StyledTableDataCell>
                                  <StyledTableDataCell>
                                    {grant.programName}
                                  </StyledTableDataCell>
                                  <StyledTableDataCell>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: "#2e7d32",
                                      }}
                                    >
                                      {grant.creditAmount}
                                    </Typography>
                                  </StyledTableDataCell>
                                  <StyledTableDataCell>
                                    {grant.appliesTo}
                                  </StyledTableDataCell>
                                  <StyledTableDataCell>
                                    {grant.eligibility}
                                  </StyledTableDataCell>
                                  <StyledTableDataCell>
                                    {grant.validUntil}
                                  </StyledTableDataCell>
                                </StyledTableRow>
                                <TableRow>
                                  <StyledTableDataCell
                                    colSpan={6}
                                    style={{ padding: 0 }}
                                  >
                                    <Collapse
                                      in={expandedRows[index]}
                                      timeout="auto"
                                      unmountOnExit
                                    >
                                      <Box
                                        sx={{
                                          padding: theme.spacing(2),
                                          backgroundColor: "#f0f0f0",
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 500,
                                            mb: 1,
                                            color: "#4b5b5d",
                                          }}
                                        >
                                          Description:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ color: "#757575" }}
                                        >
                                          {grant.description}
                                        </Typography>
                                        {grant.fullAmountDetails && (
                                          <>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontWeight: 500,
                                                mt: 1,
                                                mb: 1,
                                                color: "#4b5b5d",
                                              }}
                                            >
                                              Amount Details:
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              sx={{ color: "#757575" }}
                                            >
                                              {grant.fullAmountDetails}
                                            </Typography>
                                          </>
                                        )}
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          href={grant.actionLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{
                                            textTransform: "none",
                                            fontSize: "0.75rem",
                                            padding: "4px 8px",
                                            mt: 2,
                                          }}
                                        >
                                          Click here to Program URL
                                        </Button>
                                      </Box>
                                    </Collapse>
                                  </StyledTableDataCell>
                                </TableRow>
                              </React.Fragment>
                            ))
                          ) : (
                            <StyledTableRow>
                              <StyledTableDataCell colSpan={6} align="center">
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  No grant cost details available
                                </Typography>
                              </StyledTableDataCell>
                            </StyledTableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  </Card>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}
                >
                  <FormControl sx={{ minWidth: 120 }} size="small">
                    <InputLabel id="map-view-label">Map View</InputLabel>
                    <Select
                      labelId="map-view-label"
                      value={mapView}
                      label="Map View"
                      onChange={handleMapViewChange}
                    >
                      <MenuItem value="streetview">Street View</MenuItem>
                      <MenuItem value="roadmap">Map</MenuItem>
                      <MenuItem value="satellite">Satellite</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Title>Location View</Title>
                <MapContainer ref={mapRef} />
                {!mapLoaded && (
                  <Box sx={{ textAlign: "center", mt: 1, color: "#666" }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading map...
                  </Box>
                )}
              </Grid>

              {/* Tax Benefits Details Section */}
              {shouldShowTaxBenefits && (
                <Grid size={{ xs: 12 }}>
                  <Card>
                    <Title>Tax Benefits Details</Title>
                    <Box sx={{ overflowX: "auto" }}>
                      <Table>
                        <TableHead>
                          <TableRow
                            style={{
                              background:
                                "linear-gradient(to left, #f4efe6, #bcc4c4)",
                            }}
                          >
                            <StyledTableCell>Category</StyledTableCell>
                            <StyledTableCell>Value</StyledTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <StyledTableRow>
                            <StyledTableDataCell>
                              Business Tax Rate
                            </StyledTableDataCell>
                            <StyledTableDataCell>
                              {savedReport?.businessTaxRate ||
                                incomingState.businessTaxRate}
                              %
                            </StyledTableDataCell>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableDataCell>
                              Section 179 Limit
                            </StyledTableDataCell>
                            <StyledTableDataCell>
                              $
                              {(
                                savedReport?.section179Limit ||
                                incomingState.section179Limit
                              )?.toLocaleString()}
                            </StyledTableDataCell>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableDataCell>
                              Bonus Depreciation %
                            </StyledTableDataCell>
                            <StyledTableDataCell>
                              {savedReport?.bonusDepreciationPct ||
                                incomingState.bonusDepreciationPct}
                              %
                            </StyledTableDataCell>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableDataCell>
                              Qualifying Spend
                            </StyledTableDataCell>
                            <StyledTableDataCell>
                              $
                              {(
                                savedReport?.qualifyingSpend ||
                                incomingState.qualifyingSpend
                              )?.toLocaleString()}
                            </StyledTableDataCell>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableDataCell>
                              Estimated Tax Savings
                            </StyledTableDataCell>
                            <StyledTableDataCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: "#2e7d32" }}
                              >
                                $
                                {Math.round(
                                  savedReport?.taxSavings ||
                                    incomingState.taxSavings ||
                                    0
                                ).toLocaleString()}
                              </Typography>
                            </StyledTableDataCell>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableDataCell>
                              Upfront Cost After Tax
                            </StyledTableDataCell>
                            <StyledTableDataCell>
                              $
                              {Math.round(
                                savedReport?.upfrontAfterTax ||
                                  incomingState.upfrontAfterTax ||
                                  0
                              ).toLocaleString()}
                            </StyledTableDataCell>
                          </StyledTableRow>
                        </TableBody>
                      </Table>
                    </Box>

                    {/* Tax Disclaimer */}
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: "#fff3cd",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "#856404", fontStyle: "normal" }}
                      >
                        <strong>Tax disclaimer:</strong> Estimates are
                        illustrative only and do not constitute tax advice.
                        Section 179 and 168(k) eligibility and limits vary by
                        entity and state. Consult a qualified tax professional
                        to confirm treatment for your situation.
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Card>
                  <Title>Break Even/Cash Flow</Title>
                  <ChartContainer>
                    <canvas ref={breakEvenChartRef} />
                  </ChartContainer>
                </Card>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Card>
                  <Title>5-Year Revenue & Cost</Title>
                  <ChartContainer>
                    <canvas ref={revenueCostChartRef} />
                  </ChartContainer>
                </Card>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Card>
                  <Title>CO2 Savings & Environmental Impact</Title>
                  <ChartContainer>
                    <canvas ref={co2SavingsChartRef} />
                  </ChartContainer>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Typography color="error" variant="h6" align="center">
              No report data available. Please generate a report.
            </Typography>
          )}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={() => setSnackbarOpen(false)}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Content>
      </motion.div>
    </Container>
  );
};

export default ViewGeneratedReport;
